/**
 * Companion copy pack (#1731, spec §7.2) — the family's companion speaking in
 * first person. The companion's name is per family, so every line that would
 * name it is voiced as "I" and the name arrives through `agentName` only
 * where a label needs it. Failure lines end by pointing at a way back.
 *
 * #2109 R3-B24: every string comes from the `chat:core.*` catalog section
 * through `t`, in the reader’s UI language, and `locale` is the BCP 47 tag
 * the core formats dates with. The name is always the `{{companion}}` param.
 * A server-supplied failure reason is shown verbatim, never translated: the
 * sentence around it is one key, and the reason is its `{{reason}}` param.
 */
import i18n from 'i18next';
import type { ChatCopyPack } from '@core/config/types';
import { bcp47For } from '@/i18n/negotiate';
import type { AnyTFunction } from '@/i18n/msg';
import { asPackT, drawerChrome, exportChrome, operationalCopy, untitledFallback } from './operational';

const endsWithPunctuation = (s: string) => /[.!?]$/.test(s);

/** The reader’s declaration code (`en`, `es`, …), as the i18n runtime resolved it. */
export function activeLanguage(): string {
  return i18n.resolvedLanguage ?? i18n.language ?? 'en';
}

export function companionCopy(
  agentName: string,
  translate?: AnyTFunction,
  language: string = activeLanguage(),
): ChatCopyPack {
  const t = asPackT(translate ?? i18n.getFixedT(language));
  const locale = bcp47For(language);
  const companion = agentName;
  return {
    ...operationalCopy(t, locale, language, t('chat:core.messageList.emptyBody', { companion })),
    bubble: {
      userLabel: t('chat:core.bubble.userLabel'),
      senderNames: {
        // 'stevens' is the core's wire value for the agent turn (upstream name,
        // kept for diffability); 'assistant' is TRF's history role.
        stevens: agentName,
        assistant: agentName,
        trfer: t('chat:core.bubble.trferSender'),
      },
      senderFallback: agentName,
    },
    thinking: {
      fallbackCoachName: agentName,
      initialMessage: t('chat:core.thinking.initial'),
      progressive: [
        { atMs: 5000, text: t('chat:core.thinking.progressive.thinking') },
        { atMs: 10000, text: t('chat:core.thinking.progressive.checking') },
        { atMs: 15000, text: t('chat:core.thinking.progressive.composing') },
      ],
    },
    failures: {
      streamTimeout: {
        summary: t('chat:core.failures.streamTimeout.summary'),
        bubbleAfterPartial: t('chat:core.failures.streamTimeout.bubbleAfterPartial'),
        bubbleNoContent: t('chat:core.failures.streamTimeout.bubbleNoContent'),
      },
      refusedMessageTooLong: {
        summary: t('chat:core.failures.refusedMessageTooLong.summary'),
        bubble: t('chat:core.failures.refusedMessageTooLong.bubble'),
      },
      refused: {
        summary: (status) => t('chat:core.failures.refused.summary', { status }),
        bubble: (reason) =>
          t('chat:core.failures.refused.bubble', {
            reason: endsWithPunctuation(reason) ? reason : `${reason}.`,
          }),
      },
      notOpened: {
        summary: t('chat:core.failures.notOpened.summary'),
        bubble: t('chat:core.failures.notOpened.bubble'),
      },
      interrupted: {
        summary: t('chat:core.failures.interrupted.summary'),
        bubbleAfterPartial: t('chat:core.failures.interrupted.bubbleAfterPartial'),
        bubbleAtStart: t('chat:core.failures.interrupted.bubbleAtStart'),
      },
      sendRejected: {
        summary: (status) => t('chat:core.failures.sendRejected.summary', { status }),
        summaryNoStatus: t('chat:core.failures.sendRejected.summaryNoStatus'),
        bubble: (status) => t('chat:core.failures.sendRejected.bubble', { status }),
        bubbleNoStatus: t('chat:core.failures.sendRejected.bubbleNoStatus'),
      },
      agentError: {
        summaryFallback: t('chat:core.failures.agentError.summaryFallback'),
        bubble: (reason) => t('chat:core.failures.agentError.bubble', { reason }),
        bubbleNoReason: t('chat:core.failures.agentError.bubbleNoReason'),
      },
      connectFailed: {
        summaryFallback: t('chat:core.failures.connectFailed.summaryFallback'),
        bubble: (reason) => t('chat:core.failures.connectFailed.bubble', { reason }),
        bubbleNoReason: t('chat:core.failures.connectFailed.bubbleNoReason'),
      },
      retryNotPersisted: {
        conflict: {
          summary: t('chat:core.failures.retryNotPersisted.summary'),
          bubble: t('chat:core.failures.retryNotPersisted.conflict'),
        },
        transient: {
          summary: t('chat:core.failures.retryNotPersisted.summary'),
          bubble: t('chat:core.failures.retryNotPersisted.transient'),
        },
      },
    },
    upload: {
      unreachable: t('chat:core.upload.unreachable'),
      failedStatus: (status) => t('chat:core.upload.failedStatus', { status }),
    },
    export: {
      userLabel: t('chat:core.export.userLabel'),
      textHeaderTitle: t('chat:core.export.textHeaderTitle'),
      textHeaderSubtitle: t('chat:core.export.textHeaderSubtitle', { companion }),
      markdownTitle: t('chat:core.export.markdownTitle'),
      markdownVersionLabel: t('chat:core.export.markdownVersionLabel', { companion }),
      printPdf: t('chat:core.export.printPdf'),
      preparing: t('chat:core.export.preparing'),
      spokenSuffix: t('chat:core.export.spokenSuffix'),
      printScopeNote: t('chat:core.export.printScopeNote'),
      ...exportChrome(t),
      // Wave w1019 B7: a family's export carries no session id. An empty
      // label tells the formatters to leave the line out; the staff pack
      // (trfer.ts) keeps it for the transcript.
      labels: { ...exportChrome(t).labels, sessionId: '' },
    },
    drawer: {
      title: t('chat:core.drawer.title'),
      groups: {
        today: t('chat:core.drawer.groups.today'),
        thisWeek: t('chat:core.drawer.groups.thisWeek'),
        earlier: t('chat:core.drawer.groups.earlier'),
      },
      channelBadges: {
        telephone: t('chat:core.drawer.channels.call'),
        'web-voice': t('chat:core.drawer.channels.voice'),
        voice: t('chat:core.drawer.channels.voice'),
        whatsapp: t('chat:core.drawer.channels.whatsapp'),
        sms: t('chat:core.drawer.channels.sms'),
      },
      untitledFallback: untitledFallback(t, locale),
      ...drawerChrome(t),
    },
    actions: {
      copyMessage: t('chat:core.actions.copyMessage'),
      copied: t('chat:core.actions.copied'),
      retryMessage: t('chat:core.actions.retryMessage'),
    },
    recall: {
      line: () => t('chat:core.recall.line'),
      showDetails: t('chat:core.recall.showDetails'),
      hideDetails: t('chat:core.recall.hideDetails'),
    },
    chips: {
      regionLabel: t('chat:core.chips.regionLabel'),
    },
    voice: {
      regionLabel: t('chat:core.voice.regionLabel'),
      states: {
        requesting: t('chat:core.voice.states.requesting'),
        connecting: t('chat:core.voice.states.connecting'),
        connectingSlow: t('chat:core.voice.states.connectingSlow'),
        listening: t('chat:core.voice.states.listening'),
        speaking: t('chat:core.voice.states.speaking', { companion }),
        muted: t('chat:core.voice.states.muted'),
        ended: (durationMs) => {
          const seconds = Math.max(1, Math.round(durationMs / 1000));
          return seconds < 60
            ? t('chat:core.voice.states.endedSeconds', { count: seconds })
            : t('chat:core.voice.states.endedMinutes', { count: Math.round(seconds / 60) });
        },
      },
      failures: {
        micDenied: t('chat:core.voice.failures.micDenied'),
        micMissing: t('chat:core.voice.failures.micMissing'),
        agentAbsent: t('chat:core.voice.failures.agentAbsent'),
        unavailable: t('chat:core.voice.failures.unavailable'),
        network: t('chat:core.voice.failures.network'),
      },
      controls: {
        mute: t('chat:core.voice.controls.mute'),
        unmute: t('chat:core.voice.controls.unmute'),
        end: t('chat:core.voice.controls.end'),
        minimize: t('chat:core.voice.controls.minimize'),
        expand: t('chat:core.voice.controls.expand'),
        retry: t('chat:core.voice.controls.retry'),
      },
      duplexUnavailable: t('chat:core.voice.duplexUnavailable'),
    },
    cardCopy: {
      calibration: {
        title: t('chat:core.cardCopy.calibration.title'),
        ariaLabel: t('chat:core.cardCopy.calibration.ariaLabel'),
        approve: t('chat:core.cardCopy.calibration.approve'),
        hold: t('chat:core.cardCopy.calibration.hold'),
        delete: t('chat:core.cardCopy.calibration.delete'),
        edit: t('chat:core.cardCopy.calibration.edit'),
        clickToEdit: t('chat:core.cardCopy.calibration.clickToEdit'),
      },
    },
  };
}
